import * as React from "react";
import {
    makeStyles,
    shorthands,
    tokens,
    Tooltip,
    AppItem,
    Hamburger,
    NavDrawer,
    NavDrawerBody,
    NavDrawerHeader,
    NavItem,
    NavItemValue,
    OnNavItemSelectData
} from "@fluentui/react-components";
  import {
    InprivateAccount20Filled,
    InprivateAccount20Regular,
    DocumentEdit20Filled,
    DocumentEdit20Regular,
    BookContacts20Filled,
    BookContacts20Regular,
    PersonCircle32Regular,
    bundleIcon
  } from "@fluentui/react-icons";
import { Outlet, useLocation, useMatch, Link } from "react-router-dom";
import { flexColumn, overflowHidden, wrapperContent, wrapperContentWithBackground, drawerCollapsed, drawerContent } from "../styles/GlobalStyles";
import { mobileMaxWidth } from "../util/ResponsiveDesign";

const ImprintIcon = bundleIcon(BookContacts20Filled, BookContacts20Regular);
const EULAIcon = bundleIcon(DocumentEdit20Filled, DocumentEdit20Regular);
const PrivacyIcon = bundleIcon(InprivateAccount20Filled, InprivateAccount20Regular);

const useStyles = makeStyles({
    drawer: {
        height: "100%",
        borderRadius: "5px"
    },
    overflowHidden: overflowHidden,
    root: {
        ...flexColumn,
        ...shorthands.margin("0px"),
        backgroundColor: tokens.colorNeutralBackground1,
        gap: "0px",
        height: "100%"
    },
    content: wrapperContent,
    contentWithBackground: wrapperContentWithBackground,
    drawerExpanded: {
        height: "100%"
    },
    seemlessLink: {
        color: "unset",
        textDecoration: "none"
    },
    drawerCollapsed,
    drawerContent
});

export type SelectedPage = {
    newSelectedCategory: string | undefined;
    newSelectedValue: string;
};

export const DocsDrawer: React.FC = () => {
    const styles = useStyles();
    const location = useLocation();

    const [isOpen, setIsOpen] = React.useState(() => !window.matchMedia(`(max-width: ${mobileMaxWidth}px)`).matches);
 
    const currentLocation = location?.pathname;

    const [openCategories, setOpenCategories] = React.useState<NavItemValue[]>([]);
    const [selectedCategoryValue, setSelectedCategoryValue] = React.useState<string>("");
    const [selectedValue, setSelectedValue] = React.useState<string>(currentLocation);
    
    const handleCategoryToggle = (
        ev: Event | React.SyntheticEvent<Element, Event>,
        data: OnNavItemSelectData
    ) => {
        setSelectedCategoryValue(data.categoryValue as string);
        setSelectedValue("");
    
        if (openCategories.includes(data.categoryValue as string)) {
            setOpenCategories([]);
        } else {
            setOpenCategories([data.categoryValue as string]);
        }
    };
    
    const handleItemSelect = (
        ev: Event | React.SyntheticEvent<Element, Event>,
        data: OnNavItemSelectData
    ) => {
        setSelectedCategoryValue(data.categoryValue as string);
    };

    const match = useMatch("/docs/:type/*");

    React.useEffect(() => {
        if (match?.params.type) {
            setSelectedValue(`/docs/${match.params.type}`);
        }
    }, [match]);
    
    const hamburgerWithToolTip = React.useMemo(() => {
        return (
          <Tooltip content="Navigation" relationship="label">
            <Hamburger title={currentLocation} onClick={() => setIsOpen(!isOpen)} />
          </Tooltip>
        );
    }, [currentLocation, isOpen]);

    const navItems = React.useMemo(() => {
        return (
            <>
                <Tooltip content="General Documentation" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/docs/general">
                        <NavItem style={{ padding: "5px" }} icon={<ImprintIcon />} value="/docs/general">
                            { isOpen ? "General Documentation" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="Documentation for Publishers" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/docs/publisher">
                        <NavItem style={{ padding: "5px" }} icon={<EULAIcon />} value="/docs/publisher">
                            { isOpen ? "Documentation for Publishers" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>

                <Tooltip content="Documentation for Customers" relationship="label" positioning="after">
                    <Link className={styles.seemlessLink} to="/docs/customer">
                        <NavItem style={{ padding: "5px" }} icon={<PrivacyIcon />} value="/docs/customer">
                            { isOpen ? "Documentation for Customers" : undefined }
                        </NavItem>
                    </Link>
                </Tooltip>                        
            </>
        );
    }, [isOpen, styles.seemlessLink]);

    return (
        <div id="drawerRoot" className={styles.content}>
            <div id="drawerWrapper" className={styles.drawerExpanded}>
                <NavDrawer
                    onNavCategoryItemToggle={handleCategoryToggle}
                    onNavItemSelect={handleItemSelect}
                    openCategories={openCategories}
                    selectedValue={selectedValue}
                    selectedCategoryValue={selectedCategoryValue}
                    open={isOpen}
                    type="inline"
                    multiple={false}
                    className={styles.drawer}
                >
                    <NavDrawerHeader>{hamburgerWithToolTip}</NavDrawerHeader>

                    <NavDrawerBody>
                        <AppItem icon={<PersonCircle32Regular />}>
                            Ianus Documentation
                        </AppItem>

                        { navItems }
                    </NavDrawerBody>
                </NavDrawer>
            </div>

            {!isOpen && 
                <div className={styles.drawerCollapsed}>
                    { hamburgerWithToolTip }

                    { navItems }
                </div>
            }
            <div id="drawerContent" className={styles.drawerContent}>
                <div id="drawerOutlet" className={styles.contentWithBackground}>
                    <Outlet />
                </div>
            </div>
        </div>
    );
};